// apps/codeframe/src/chrome/timeline/TimelineRuler.tsx
import { useEffect, useRef, useState } from 'react';
import { getPlayer } from '../../motion/registry';
import { driveLottie } from './lottieTransport';
import { rulerTicks, xToTime, snapTime } from './timeMap';
import type { MotionTimeline } from '../../store/types';

/**
 * Time axis with ticks + a draggable playhead. `width` is the lane area width
 * in px (provided by the parent grid); scrubbing seeks the live player.
 */
export function TimelineRuler({
  frameId,
  timeline,
  width,
}: {
  frameId: string;
  timeline: MotionTimeline;
  width: number;
}) {
  const [head, setHead] = useState(0);
  const draggingRef = useRef(false);

  // track the player time when not dragging
  useEffect(() => {
    const id = setInterval(() => {
      if (draggingRef.current) return;
      const p = getPlayer(frameId);
      if (p) setHead(p.time);
    }, 60);
    return () => clearInterval(id);
  }, [frameId]);

  const ticks = rulerTicks(timeline.duration, width);
  const headX = width > 0 && timeline.duration > 0 ? (head / timeline.duration) * width : 0;

  const seekToClientX = (clientX: number, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const t = snapTime(xToTime(clientX - rect.left, timeline.duration, width), timeline.fps);
    setHead(t);
    getPlayer(frameId)?.seek(t);
    driveLottie(frameId, 'seek', t);
  };

  const onDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const el = e.currentTarget;
    getPlayer(frameId)?.setScrubbing(true);
    draggingRef.current = true;
    seekToClientX(e.clientX, el);
    const onMove = (ev: MouseEvent) => seekToClientX(ev.clientX, el);
    const onUp = () => {
      draggingRef.current = false;
      getPlayer(frameId)?.setScrubbing(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div
      className="relative h-7 cursor-pointer select-none border-b border-[var(--color-chip-border)] font-[family-name:var(--font-mono)]"
      style={{ width }}
      onMouseDown={onDown}
      role="slider"
      aria-label="Playhead"
      aria-valuemin={0}
      aria-valuemax={timeline.duration}
      aria-valuenow={head}
    >
      {ticks.map((tk, i) => (
        <div key={i} className="absolute top-0 bottom-0" style={{ left: tk.x }}>
          <div className={`w-px ${tk.major ? 'h-3 bg-[var(--color-text-muted)]' : 'h-1.5 bg-[var(--color-chip-border)]'}`} />
          {tk.major && (
            <span className="absolute top-3 -translate-x-1/2 text-[9px] tabular-nums text-[var(--color-text-muted)]">
              {(tk.t / 1000).toFixed(tk.t % 1000 === 0 ? 0 : 1)}s
            </span>
          )}
        </div>
      ))}
      {/* playhead */}
      <div className="pointer-events-none absolute top-0 bottom-0 w-px bg-[var(--color-accent)]" style={{ left: headX }}>
        <div className="absolute -left-[3px] top-0 h-[7px] w-[7px] rotate-45 bg-[var(--color-accent)]" />
      </div>
    </div>
  );
}
